import { useMutation, useQuery } from "urql";
import { graphql } from "../../../../__generated__/gql";
import Modal from "../../../../ui/Modal/Modal";
import { useCallback, useEffect, useState } from "react";
import GenerateModal, {
  GenerateArticleModalFormData,
} from "../../../../components/GenerateModal/GenerateModal";
import { useToast } from "../../../../ui/Toast/ToastContext";
import useUser from "../../../../providers/UserProvider/useUser";
import InsufficientCreditsModal from "../../../../components/InsufficientCreditsModal/InsufficientCreditsModal";
import Textarea from "../../../../ui/Textarea/Textarea";
import Label from "../../../../ui/Label/Label";
import styles from "./GenerateArticleModal.module.scss";
import Switch from "../../../../ui/Switch/Switch";
import ConfirmationModal from "../../../../ui/Modal/ConfirmationModal";
import { ArticleStatus } from "../../../../__generated__/gql/graphql";

function UnsavedChangesModal({
  onClose,
  onDiscard,
}: {
  onClose: () => void;
  onDiscard?: () => void | Promise<void>;
}) {
  const [loading, setLoading] = useState(false);

  const onProceed = useCallback(async () => {
    setLoading(true);
    await onDiscard?.();
    setLoading(false);
  }, [onDiscard]);

  return (
    <ConfirmationModal
      onClose={onClose}
      onSubmit={onProceed}
      loading={loading}
      confirmationText="Discard"
      destructive
    />
  );
}

const GenerateArticleModalQuery = graphql(/* GraphQL */ `
  query generateArticleModal(
    $id: String!
    $regenerateBrief: Boolean
    $regenerateOutline: Boolean
    $regenerateContent: Boolean
    $regenerateLinks: Boolean
  ) {
    articleCounts(input: { filters: [{ ids: [$id] }] }) {
      total
      processing
      generated
      published
      draft
    }
    article(id: $id) {
      id
      additionalPrompt
      status
    }
    creditsRequired(
      input: {
        filters: [{ ids: [$id] }]
        regenerateBrief: $regenerateBrief
        regenerateOutline: $regenerateOutline
        regenerateContent: $regenerateContent
        regenerateLinks: $regenerateLinks
      }
    )
  }
`);

const GenerateArticleMutation = graphql(/* GraphQL */ `
  mutation generateArticle($input: GenerateArticleInput!) {
    generateArticle(input: $input) {
      ... on Article {
        id
        status
      }
      ... on InsufficientCreditsError {
        __typename
        message
      }
    }
  }
`);

const UpdateArticleAdditionalPromptMutation = graphql(/* GraphQL */ `
  mutation updateArticleAdditionalPrompt(
    $id: String!
    $additionalPrompt: String!
  ) {
    updateArticle(input: { id: $id, additionalPrompt: $additionalPrompt }) {
      id
      additionalPrompt
    }
  }
`);

function InnerGenerateArticleModal({
  isOpen,
  onClose,
  id,
  articleCounts,
  article,
  creditsRequired,
  onChange,
  fetching,
}: {
  isOpen: boolean;
  onClose: () => void;
  id: string;
  articleCounts: {
    total: number;
    processing: number;
    generated: number;
    published: number;
    draft: number;
  };
  article: { additionalPrompt?: string | null; status: ArticleStatus };
  creditsRequired: number;
  onChange: (data: {
    regenerateBrief?: boolean;
    regenerateOutline?: boolean;
    regenerateContent?: boolean;
    regenerateLinks?: boolean;
  }) => void;
  fetching: boolean;
}) {
  const { showToast } = useToast();

  const [addFeedback, setAddFeedback] = useState(!!article.additionalPrompt);
  const [additionalPrompt, setAdditionalPrompt] = useState<string>(
    article.additionalPrompt ?? "",
  );

  const [generateArticleResponse, generateArticle] = useMutation(
    GenerateArticleMutation,
  );
  const [updateArticleAdditionalPromptResponse, updateArticleAdditionalPrompt] =
    useMutation(UpdateArticleAdditionalPromptMutation);

  const onSubmit = useCallback(
    async (data: GenerateArticleModalFormData) => {
      if (addFeedback && additionalPrompt !== article.additionalPrompt) {
        await updateArticleAdditionalPrompt({
          id,
          additionalPrompt,
        });
      }

      if (!addFeedback) {
        await updateArticleAdditionalPrompt({
          id,
          additionalPrompt: "",
        });
      }

      const generateArticleResponse = await generateArticle({
        input: {
          id,
          regenerateBrief: data.regenerateBrief,
          regenerateOutline: data.regenerateOutline,
          regenerateContent: data.regenerateContent,
          regenerateLinks: data.regenerateLinks,
        },
      });

      if (
        generateArticleResponse.data?.generateArticle.__typename ===
        "InsufficientCreditsError"
      ) {
        showToast({
          variant: "error",
          title: "You've run out of article credits",
        });
      } else {
        showToast({ title: "Article queued for generation" });
      }
      onClose();
    },
    [
      addFeedback,
      additionalPrompt,
      article.additionalPrompt,
      generateArticle,
      id,
      onClose,
      showToast,
      updateArticleAdditionalPrompt,
    ],
  );

  return (
    <>
      <GenerateModal
        onClose={onClose}
        onSubmit={onSubmit}
        fetching={
          fetching ||
          generateArticleResponse.fetching ||
          updateArticleAdditionalPromptResponse.fetching ||
          !isOpen
        }
        articleCounts={articleCounts}
        creditsRequired={creditsRequired}
        onChange={onChange}
      >
        <label htmlFor="add-feedback" className={styles.switch}>
          <Switch
            id="add-feedback"
            checked={addFeedback}
            onCheckedChange={() =>
              setAddFeedback((previousValue) => !previousValue)
            }
          />
          <span className={styles.switchLabel}>
            {article.status === ArticleStatus.Draft
              ? "Add custom prompt"
              : "Add feedback"}
          </span>
        </label>
        {addFeedback && (
          <Label
            label={
              article.status === ArticleStatus.Draft
                ? "Your custom prompt"
                : "Your feedback"
            }
            className={styles.feedback}
            tooltip="Add as much specific detail as you'd like to help Macaw improve the article. You can also head over generation settings to set global information to improve quality and accuracy across all articles."
          >
            <Textarea
              placeholder="Add specific details or context"
              value={additionalPrompt}
              onChange={(e) => setAdditionalPrompt(e.target.value)}
            />
          </Label>
        )}
      </GenerateModal>
    </>
  );
}

function GenerateArticleModal({
  isOpen,
  onClose,
  id,
  isDirty,
  onDiscard,
}: {
  isOpen: boolean;
  onClose: () => void;
  id: string;
  isDirty?: boolean;
  onDiscard?: () => void | Promise<void>;
}) {
  const { billingInfo, articlesRemaining } = useUser();
  const [variables, setVariables] = useState({
    regenerateBrief: false,
    regenerateOutline: false,
    regenerateContent: true,
    regenerateLinks: true,
  });

  const [generateArticleModalResponse] = useQuery({
    query: GenerateArticleModalQuery,
    pause: !isOpen,
    variables: {
      id,
      ...variables,
    },
  });

  const [articlesRemainingOnOpen, setArticlesRemainingOnOpen] =
    useState(articlesRemaining);

  useEffect(() => {
    if (isOpen) {
      setArticlesRemainingOnOpen(articlesRemaining);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onChange = useCallback(
    (values: {
      regenerateBrief?: boolean;
      regenerateOutline?: boolean;
      regenerateContent?: boolean;
      regenerateLinks?: boolean;
    }) =>
      setVariables((previous) => ({
        ...previous,
        ...values,
      })),
    [],
  );

  const articleCounts = generateArticleModalResponse.data?.articleCounts;
  const article = generateArticleModalResponse.data?.article;
  const creditsRequired = generateArticleModalResponse.data?.creditsRequired;

  if (!billingInfo || typeof creditsRequired !== "number") {
    return null;
  }

  if (creditsRequired && creditsRequired > articlesRemainingOnOpen) {
    return (
      <InsufficientCreditsModal
        isOpen={isOpen}
        onClose={onClose}
        articleCount={articleCounts?.total ?? 0}
      />
    );
  }

  const isRegeneration = !!(
    articleCounts &&
    articleCounts?.generated + articleCounts?.published === articleCounts?.total
  );

  const title = `Confirm ${
    isRegeneration ? "regeneration" : "generation"
  } of article?`;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isDirty ? "Discard changes?" : title}
      subtitle={
        isDirty
          ? "You have unsaved changes that will be discarded if you continue."
          : undefined
      }
      loading={
        (generateArticleModalResponse.fetching &&
          !generateArticleModalResponse.data) ||
        !generateArticleModalResponse.data?.articleCounts
      }
    >
      {isDirty ? (
        <UnsavedChangesModal onClose={onClose} onDiscard={onDiscard} />
      ) : (
        articleCounts &&
        article && (
          <InnerGenerateArticleModal
            isOpen={isOpen}
            onClose={onClose}
            id={id}
            articleCounts={articleCounts}
            article={article}
            creditsRequired={creditsRequired}
            onChange={onChange}
            fetching={generateArticleModalResponse.fetching}
          />
        )
      )}
    </Modal>
  );
}

export default GenerateArticleModal;
